/**
 * StaggeredFadeIn – Fade-in + slide-up for list/grid items on mount.
 *
 * Performance: useNativeDriver: true (UI thread). Stagger only for first ~10 items;
 * beyond that, items fade in quickly with no delay so scrolling stays smooth.
 *
 * Android: the entrance animation is disabled entirely. The native-driver mount
 * animation races FlashList row recycling under Fabric and surfaces as the
 * Android-16 fatal `RetryableMountingLayerException: Unable to find viewState for
 * tag N` in SurfaceMountingManager.getViewState. Removing the post-mount mutation
 * on recyclable rows removes the trigger. The fade is purely cosmetic, so Android
 * renders a static View while iOS/web keep the animation.
 */

import React, { useEffect, useRef } from 'react'
import { Animated, Easing, Platform, View, type ViewStyle } from 'react-native'

const ANIMATION_DISABLED_PLATFORM = Platform.OS === 'android'

const STAGGER_MS = 35
const STAGGER_CAP_INDEX = 16
const DURATION_MS = 260

export interface StaggeredFadeInProps {
  /** Item index: 0–15 get stagger; 16+ get instant fade (no delay) */
  index: number
  children: React.ReactNode
  style?: ViewStyle
  testID?: string
  /** When true, render a static View (no fade/slide) — Storybook / tests / reduced motion. */
  disableAnimation?: boolean
}

const StaggeredFadeInAnimated: React.FC<
  Pick<StaggeredFadeInProps, 'index' | 'children' | 'style' | 'testID'>
> = ({ index, children, style, testID }) => {
  const opacity = useRef(new Animated.Value(0)).current
  const translateY = useRef(new Animated.Value(12)).current

  useEffect(() => {
    opacity.setValue(0)
    translateY.setValue(12)
    const delay = index < STAGGER_CAP_INDEX ? index * STAGGER_MS : 0
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: DURATION_MS,
        delay,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: DURATION_MS,
        delay,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start()
  }, [opacity, translateY, index])

  return (
    <Animated.View
      style={[style, { opacity, transform: [{ translateY }] }]}
      testID={testID}
    >
      {children}
    </Animated.View>
  )
}

export const StaggeredFadeIn: React.FC<StaggeredFadeInProps> = ({
  index,
  children,
  style,
  testID,
  disableAnimation = false,
}) => {
  if (disableAnimation || ANIMATION_DISABLED_PLATFORM) {
    return (
      <View style={style} testID={testID}>
        {children}
      </View>
    )
  }

  return (
    <StaggeredFadeInAnimated index={index} style={style} testID={testID}>
      {children}
    </StaggeredFadeInAnimated>
  )
}
